import React, { useState } from "react";
import { Button, Glyphicon, ControlLabel } from "react-bootstrap";
import moment from "moment";
import translator from "../../../components/localization/translator";
import Notifier from "../../../components/message/Notifier";
import security from "../../../components/common/security/security";
import { SANITARY_BOOK_STATUSES } from "../../../config/constants";
import SanitaryBookReviewModal from "./SanitaryBookReviewModal";
import {
    getBooks, REVIEWER_ROLES,
} from "./sanitaryBookHelpers";

const SanitaryBook = ({ distributorId, onFetchSanitaryBook, onReview }) => {
    const [books, setBooks] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeEndDate, setActiveEndDate] = useState("");
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectEditor, setShowRejectEditor] = useState(false);
    const [saving, setSaving] = useState(false);

    const currentUser = security.getUser();
    const isReviewer = Boolean(currentUser && REVIEWER_ROLES.indexOf(currentUser.role) !== -1);
    const book = books[selectedIndex];
    const status = SANITARY_BOOK_STATUSES.statusById(book ? book.status : undefined) || {};
    const isReviewable = Boolean(book) && isReviewer && book.status === SANITARY_BOOK_STATUSES.PENDING;
    const showActions = Boolean(book) && isReviewer;

    const selectBook = (index) => {
        const next = books[index];
        setSelectedIndex(index);
        setActiveEndDate(next && next.activeEndDate
            ? moment(next.activeEndDate).format("YYYY-MM-DD")
            : "");
        setRejectReason(next ? next.rejectReason || "" : "");
        setShowRejectEditor(false);
    };

    const handleView = () => {
        if (!distributorId) {
            return;
        }
        setLoading(true);
        onFetchSanitaryBook(distributorId)
            .then((response) => {
                const list = getBooks(response && response.data);
                if (!list.length) {
                    setLoading(false);
                    Notifier.info("_sanitary_book_not_submitted_", "_sanitary_book_");
                    return;
                }
                const initialIndex = list.length - 1;
                setBooks(list);
                setSelectedIndex(initialIndex);
                setActiveEndDate(list[initialIndex].activeEndDate
                    ? moment(list[initialIndex].activeEndDate).format("YYYY-MM-DD")
                    : "");
                setRejectReason(list[initialIndex].rejectReason || "");
                setShowRejectEditor(false);
                setLoading(false);
                setShowModal(true);
            })
            .catch(() => {
                setLoading(false);
                Notifier.error("_something_went_wrong_", "_sanitary_book_");
            });
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const openRejectEditor = () => {
        setShowRejectEditor(true);
        setRejectReason("");
    };

    const closeRejectEditor = () => {
        setShowRejectEditor(false);
        setRejectReason(book.rejectReason || "");
    };

    const handleApprove = () => {
        if (!book || !book._id) {
            return;
        }
        if (!activeEndDate) {
            Notifier.warning(translator.translate("_sanitary_book_validity_date_required_"));
            return;
        }
        setSaving(true);
        onReview(book._id, {
            status: SANITARY_BOOK_STATUSES.APPROVED,
            activeEndDate,
        }, distributorId).then(() => {
            setSaving(false);
            setShowRejectEditor(false);
            setBooks(prev => prev.map(item => item._id === book._id
                ? { ...item, status: SANITARY_BOOK_STATUSES.APPROVED }
                : item));
            Notifier.success("_sanitary_book_approved_success_", "_sanitary_book_");
        }).catch(() => {
            setSaving(false);
        });
    };

    const handleReject = () => {
        if (!book || !book._id) {
            return;
        }
        if (!rejectReason) {
            Notifier.warning(translator.translate("_sanitary_book_reject_reason_required_"));
            return;
        }
        setSaving(true);
        onReview(book._id, {
            status: SANITARY_BOOK_STATUSES.REJECTED,
            rejectReason: Number(rejectReason),
        }, distributorId).then(() => {
            setSaving(false);
            setShowRejectEditor(false);
            setBooks(prev => prev.map(item => item._id === book._id
                ? {
                    ...item,
                    status: SANITARY_BOOK_STATUSES.REJECTED,
                    rejectReason: Number(rejectReason),
                }
                : item));
            Notifier.success("_sanitary_book_rejected_success_", "_sanitary_book_");
        }).catch(() => {
            setSaving(false);
        });
    };

    return (
        <div style={{ padding: "20px 0", borderBottom: "1px solid #eee" }}>
            <div className="display-flex" style={{ gap: 10 }}>
                <ControlLabel className="label-width" style={{ fontWeight: 600 }}>
                    {translator.translate("_sanitary_book_")}
                </ControlLabel>
                <Button bsSize="xsmall" bsStyle="default" disabled={loading} onClick={handleView}>
                    {loading
                        ? <Glyphicon glyph="refresh" />
                        : <Glyphicon glyph="eye-open" />} {translator.translate("_sanitary_book_view_pages_")}
                </Button>
            </div>
            <SanitaryBookReviewModal
                show={showModal}
                onClose={closeModal}
                books={books}
                selectedIndex={selectedIndex}
                onSelectBook={selectBook}
                status={status}
                activeEndDate={activeEndDate}
                onActiveEndDateChange={setActiveEndDate}
                isReviewable={isReviewable}
                showActions={showActions}
                saving={saving}
                onApprove={handleApprove}
                onOpenRejectEditor={openRejectEditor}
                onCloseRejectEditor={closeRejectEditor}
                showRejectEditor={showRejectEditor}
                rejectReason={rejectReason}
                onRejectReasonChange={setRejectReason}
                onConfirmReject={handleReject}
            />
        </div>
    );
};

export default SanitaryBook;
